/**
 * @module builder
 */
import type { QuoteDocument, AreaSubstrate, OptionsDocument } from 'paintscout'
import findIndex from 'lodash/findIndex'
import { updateSubstrateDescriptions } from '../substrates/update-substrate-descriptions/update-substrate-descriptions'

import { calculateQuote } from '../util/calculate/calculate-quote'
import { generateAreaDescription } from '../util/generate-area-description/generate-area-description'
import { getItemSection } from '../util/get-item-section/get-item-section'
import { moveToBid } from '../util/move-to/move-to-bid'
import { uuid } from '../../util/uuid'
import { removeFalseValues } from '../../../util/removeFalseValues'
import { getAreaSubstrate } from './get-area-substrate'
import { keyAreaSubstrates } from './key-area-substrates'
import { toAreaSubstrate, createBaseAreaSubstrate } from './util'
import type { UpdateableAreaSubstrate } from '.'

/**
 * Update areaSubstrate on quote, returning UpdatedQuote.
 * If areaSubstrate does not exist, create it.
 */
export function updateAreaSubstrate(args: {
  quote: QuoteDocument
  options: OptionsDocument
  areaKey: string
  areaSubstrate: UpdateableAreaSubstrate
  calculate?: boolean
  sanitize?: boolean
}): QuoteDocument {
  const { areaKey, options, areaSubstrate, calculate, sanitize = true } = args
  const key = areaSubstrate.key ? areaSubstrate.key : uuid()

  // Add areaKey if not present
  const quote = args.quote.areas[areaKey]?.substrateOrder
    ? args.quote
    : keyAreaSubstrates({ quote: args.quote, areaKey })

  // Check for existing areaSubstrate, create new if not found
  let baseAreaSubstrate = getAreaSubstrate({
    quote,
    options,
    areaKey,
    key: areaSubstrate.key,
    full: true
  })

  let isNewItem = false
  if (!baseAreaSubstrate) {
    baseAreaSubstrate = createBaseAreaSubstrate(key)
    isNewItem = true
  }

  // Merge updated areaSubstrate with itself before changes/new areaSubstrate
  // Make sure is set to correct section
  const convertedAreaSubstrate = toAreaSubstrate(baseAreaSubstrate, areaSubstrate)

  // Reset the hours value when switching from value per hour to price per amount
  if (
    areaSubstrate.rate?.calculationType === 'pricePerAmount' &&
    baseAreaSubstrate.rate?.calculation_type === 'value_per_hour'
  ) {
    convertedAreaSubstrate.default_hours = 0
    convertedAreaSubstrate.override_hours = false
  }

  const initialSection = convertedAreaSubstrate?.section?.value ?? 'custom'
  const newSection = convertedAreaSubstrate?.rate?.section ?? 'custom'

  convertedAreaSubstrate.section.value = newSection

  // We need to keep some values as false instead of removing them
  // because they are explicitly false checked elsewhere
  const sanitizedAreaSubstrate = sanitize
    ? removeFalseValues<AreaSubstrate>(convertedAreaSubstrate, [
        'show',
        'show_crew',
        'use_total',
        'override_length',
        'override_height',
        'override_width'
      ])
    : convertedAreaSubstrate

  // Add substrate to area.substrates, or update existing, do same for order
  const sectionSubstrates = quote.areas[areaKey].substrates[newSection]
    ? [...quote.areas[areaKey].substrates[newSection]] // prevent mutation on quote by doing [...]
    : []
  if (isNewItem || initialSection !== newSection) {
    sectionSubstrates.push(sanitizedAreaSubstrate)
  } else {
    const index = findIndex(sectionSubstrates, { key })
    sectionSubstrates[index] = sanitizedAreaSubstrate
  }

  const substrateOrder = quote.areas[areaKey]?.substrateOrder

  const orderIndex = findIndex(substrateOrder, { key })
  if (orderIndex === -1) {
    substrateOrder.push({
      type: 'areaSubstrate',
      key
    })
  }

  // Update Quote with new area.substrates and orders
  let updatedQuote = {
    ...quote,
    areas: {
      ...quote.areas,
      [areaKey]: {
        ...quote.areas[areaKey],
        substrates: {
          ...quote.areas[areaKey].substrates,
          [newSection]: sectionSubstrates
        },
        substrateOrder,
        substrate_string: {
          ...quote.areas[areaKey].substrate_string
        }
      }
    },
    substrates: {
      ...quote.substrates
    }
  }
  if (getItemSection(sanitizedAreaSubstrate) === 'bid' && getItemSection(updatedQuote.areas[areaKey]) !== 'bid') {
    updatedQuote.areas[areaKey] = moveToBid(updatedQuote.areas[areaKey])
  }

  // If in new section, remove from old section, update the substrate_string
  if (initialSection !== newSection) {
    const index = findIndex(updatedQuote.areas[areaKey].substrates[initialSection], { key })
    updatedQuote.areas[areaKey]?.substrates[initialSection]?.splice(index, 1)
  }

  updatedQuote = calculate === false ? updatedQuote : calculateQuote({ quote: updatedQuote, options })

  updatedQuote.areas[areaKey].substrate_string.default = generateAreaDescription({
    quote: updatedQuote,
    options,
    areaKey
  })

  // Update substrates to reflect changes made in the updatedAreaSubstrate
  updatedQuote.substrates = updateSubstrateDescriptions({ quote: updatedQuote, options })

  return updatedQuote
}
